/**
 * The My Documents refinement row: Type · Visibility · Status.
 *
 * Three INDEPENDENT facets that AND together, so "Signed + Private + Action
 * required" is expressible. They used to share one flat single-select strip
 * with the location tabs, which meant choosing a visibility silently cleared
 * the type — three axes fighting over one selection.
 *
 * Refines My Documents only; Archive and Shared are locations, not filters.
 * Purely presentational — the state lives in `useNodeListing`.
 */

import { AlertTriangle, Globe, LayoutGrid, Lock, PenTool, FileText, UserCheck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { TabPill } from './TabPill.jsx';
import { TAB_PILL_TRANSITION } from './tabPillTransition.js';

const PILL =
  'relative px-3 py-1 rounded-[4px] text-[13px] font-medium transition-colors ' +
  'flex items-center gap-1.5 cursor-pointer';

function FacetGroup({ label, options, value, onChange, layoutId }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </span>
      <div className="inline-flex items-center gap-1">
        {options.map((option) => {
          const active = value === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange(option.value)}
              className={cn(PILL, active ? 'text-white' : 'text-muted-foreground hover:bg-muted/70')}
            >
              {active && (
                <TabPill
                  layoutId={layoutId}
                  transition={TAB_PILL_TRANSITION}
                  className="absolute inset-0 rounded-[4px] bg-brand"
                />
              )}
              <span className="relative z-10 flex items-center gap-1.5">
                {option.icon}
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const Divider = () => <div className="h-5 w-px bg-border" />;

export function FacetBar({
  typeFilter,
  onTypeChange,
  visFilter,
  onVisibilityChange,
  actionOnly,
  onActionToggle,
  actionCount = 0,
  hasActiveFacets,
  onClear,
}) {
  const { t } = useTranslation(['mainPage']);

  const typeOptions = [
    { value: 'all', label: t('mainPage:facets.typeAll') || 'All', icon: <LayoutGrid className="w-3.5 h-3.5" /> },
    { value: 'ordinary', label: t('mainPage:facets.ordinary') || 'Ordinary', icon: <FileText className="w-3.5 h-3.5" /> },
    { value: 'agreement', label: t('mainPage:facets.agreements') || 'Agreements', icon: <UserCheck className="w-3.5 h-3.5" /> },
    { value: 'signed', label: t('mainPage:facets.signed') || 'Signed', icon: <PenTool className="w-3.5 h-3.5" /> },
  ];
  const visibilityOptions = [
    { value: 'any', label: t('mainPage:facets.visAny') || 'Any' },
    { value: 'private', label: t('mainPage:facets.private') || 'Private', icon: <Lock className="w-3.5 h-3.5" /> },
    { value: 'public', label: t('mainPage:facets.public') || 'Public', icon: <Globe className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="flex items-center gap-x-5 gap-y-2 flex-wrap">
      <FacetGroup
        label={t('mainPage:facets.type') || 'Type'}
        options={typeOptions}
        value={typeFilter}
        onChange={onTypeChange}
        layoutId="docs-type-pill"
      />
      <Divider />
      <FacetGroup
        label={t('mainPage:facets.visibility') || 'Visibility'}
        options={visibilityOptions}
        value={visFilter}
        onChange={onVisibilityChange}
        layoutId="docs-vis-pill"
      />
      <Divider />

      {/* Status is an on/off toggle, not a member of either single-select
          group — dashed so it does not read as one. Echoes the amber banner. */}
      <button
        type="button"
        aria-pressed={actionOnly}
        onClick={onActionToggle}
        className={cn(
          PILL,
          'border border-dashed',
          actionOnly
            ? 'border-amber-400 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-700'
            : 'border-border text-muted-foreground hover:bg-muted/70',
        )}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        {t('mainPage:facets.actionRequired') || 'Action required'}
        {actionCount > 0 && (
          <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-semibold bg-red-500 text-white">
            {actionCount}
          </span>
        )}
      </button>

      {hasActiveFacets && (
        <button
          type="button"
          onClick={onClear}
          className="ml-auto text-[13px] font-medium text-muted-foreground hover:text-foreground flex items-center gap-1 cursor-pointer"
        >
          <X className="w-3.5 h-3.5" />
          {t('mainPage:facets.clear') || 'Clear filters'}
        </button>
      )}
    </div>
  );
}
